import { useToast } from 'vue-toastification';
import axios from '@/config/axios';
import { authorApi } from '../authors/authors';
import { categoryApi } from '../categories/categories';
import { translatorApi } from '../translators/translators';

const toast = useToast();

function removeAccents(str) {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

function unwrap(res) {
  const { status, message, result } = res.data;
  if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
  return result;
}

const bookSuggestApi = {
  getList: (page = 0, status, keyword) => {
    const params = { page };
    if (status !== undefined && status !== 'ALL') params.status = status;
    if (keyword && keyword.trim()) params.keyword = keyword.trim();
    return axios.get('book-suggest/v2/get-list', { params }).then(unwrap);
  },
  countPending: () => axios.get('book-suggest/v2/count-pending').then(unwrap),
  reject: (payload) => axios.post('book-suggest/v2/reject', payload).then(unwrap),
  createFromSuggest: (formData) => axios.post('book/v2/create-from-suggest', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(unwrap),
};

export default {
  name: 'AdminBookSuggests',
  data() {
    return {
      // List
      suggests: [],
      isLoading: false,
      currentPage: 0,
      totalPages: 0,
      totalElements: 0,
      pageSize: 10,
      filterStatus: 'ALL',
      searchKeyword: '',
      searchDebounceTimer: null,
      pendingCount: 0,
      activeDropdown: '',

      // Modals
      selectedSuggest: null,
      detailModalVisible: false,
      approveModalVisible: false,
      rejectModalVisible: false,
      rejectNote: '',
      isSubmitting: false,

      // Create Book Form (in approve modal)
      bookForm: {
        name: '',
        content: '',
        coverFile: null,
        coverPreview: null,
        selectedAuthors: [],
        selectedCategories: [],
        selectedTranslators: [],
        linkShopee: ''
      },
      bookFormErrors: { name: '', content: '', authors: '', categories: '' },
      dragOver: false,

      // Author/Category/Translator search for approve form
      authors: [],
      categories: [],
      translators: [],
      searchAuthorModal: '',
      searchCategoryModal: '',
      searchTranslatorModal: '',

      // Status options
      statusOptions: [
        { value: 'ALL', label: 'Tất cả trạng thái' },
        { value: 0, label: 'Chờ duyệt' },
        { value: 1, label: 'Đã duyệt' },
        { value: 2, label: 'Từ chối' }
      ]
    };
  },
  computed: {
    currentStatusLabel() {
      const opt = this.statusOptions.find(o => o.value === this.filterStatus);
      return opt ? opt.label : 'Tất cả trạng thái';
    },
    filteredSuggests() {
      return this.suggests;
    },
    visiblePages() {
      const range = [];
      const maxVisible = 5;
      let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible);
      if (end - start < maxVisible) start = Math.max(0, end - maxVisible);
      for (let i = start; i < end; i++) range.push(i);
      return range;
    },
    sortedAuthors() {
      const selectedIds = this.bookForm.selectedAuthors.map(a => a.id);
      let list = this.authors.filter(a => !selectedIds.includes(a.id));
      const rawQ = (this.searchAuthorModal || '').trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(a => {
          const n = a.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    },
    sortedCategories() {
      const selectedIds = this.bookForm.selectedCategories.map(c => c.id);
      let list = this.categories.filter(c => !selectedIds.includes(c.id));
      const rawQ = (this.searchCategoryModal || '').trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(c => {
          const n = c.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    },
    sortedTranslators() {
      const selectedIds = this.bookForm.selectedTranslators.map(t => t.id);
      let list = this.translators.filter(t => !selectedIds.includes(t.id));
      const rawQ = (this.searchTranslatorModal || '').trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(t => {
          const n = t.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    }
  },
  watch: {
    searchKeyword(newVal, oldVal) {
      if (newVal === oldVal) return;
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.loadSuggests(0);
      }, 400);
    }
  },
  mounted() {
    this.loadSuggests(0);
    this.fetchPendingCount();
    this.fetchAuthors();
    this.fetchCategories();
    this.fetchTranslators();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    clearTimeout(this.searchDebounceTimer);
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    handleDocumentClick(e) {
      if (!e.target.closest('.filter-custom-dropdown')) this.activeDropdown = '';
    },
    toggleDropdown(name) {
      this.activeDropdown = this.activeDropdown === name ? '' : name;
    },
    selectStatus(val) {
      this.filterStatus = val;
      this.activeDropdown = '';
      this.loadSuggests(0);
    },

    // ── LOAD DATA ───────────────────────────────────────────────────────────
    async loadSuggests(page = 0) {
      this.isLoading = true;
      try {
        const result = await bookSuggestApi.getList(page, this.filterStatus, this.searchKeyword);
        this.suggests = result.content || [];
        this.currentPage = result.number || 0;
        this.totalPages = result.totalPages || 0;
        this.totalElements = result.totalElements || 0;
      } catch (err) {
        toast.error('Lỗi tải danh sách đề xuất.');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPendingCount() {
      try {
        this.pendingCount = await bookSuggestApi.countPending() || 0;
      } catch (e) { /* silent */ }
    },
    async fetchAuthors() {
      try {
        const result = await authorApi.getList(0);
        const all = result.content || result || [];
        this.authors = all.filter(a => a.status === 0);
      } catch (e) { /* silent */ }
    },
    async fetchCategories() {
      try {
        const result = await categoryApi.getList(0);
        const all = result.content || result || [];
        this.categories = all.filter(c => c.status === 0);
      } catch (e) { /* silent */ }
    },
    async fetchTranslators() {
      try {
        const result = await translatorApi.getList(0);
        const all = result.content || result || [];
        this.translators = all.filter(t => t.status === 0);
      } catch (e) { /* silent */ }
    },

    // ── DETAIL MODAL ─────────────────────────────────────────────────────────
    openDetailModal(item) {
      this.selectedSuggest = item;
      this.detailModalVisible = true;
    },
    closeDetailModal() {
      this.detailModalVisible = false;
      this.selectedSuggest = null;
    },

    // ── APPROVE MODAL ────────────────────────────────────────────────────────
    openApproveModal(item) {
      this.selectedSuggest = item;
      this.resetBookForm(item);
      this.approveModalVisible = true;
      this.$nextTick(() => this.$refs.approveBookNameInput?.focus());
    },
    openApproveFromDetail() {
      this.detailModalVisible = false;
      this.$nextTick(() => {
        this.resetBookForm(this.selectedSuggest);
        this.approveModalVisible = true;
      });
    },
    closeApproveModal() {
      this.approveModalVisible = false;
      this.resetBookForm(null);
    },
    resetBookForm(suggest) {
      this.bookForm = {
        name: suggest ? suggest.bookName : '',
        content: suggest ? (suggest.bookContent || '') : '',
        coverFile: null,
        coverPreview: null,
        selectedAuthors: [],
        selectedCategories: [],
        selectedTranslators: [],
        linkShopee: ''
      };
      this.bookFormErrors = { name: '', content: '', authors: '', categories: '' };
      this.dragOver = false;
      this.searchAuthorModal = '';
      this.searchCategoryModal = '';
      this.searchTranslatorModal = '';
    },

    // Cover file handling
    onCoverDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) this.setCoverFile(file);
    },
    onCoverFileChange(e) {
      const file = e.target.files?.[0];
      if (file) this.setCoverFile(file);
    },
    setCoverFile(file) {
      this.bookForm.coverFile = file;
      this.bookForm.coverPreview = URL.createObjectURL(file);
    },

    // Author / Category / Translator helpers
    toggleAuthor(a) {
      if (this.bookForm.selectedAuthors.some(x => x.id === a.id)) {
        this.removeAuthor(a);
      } else {
        this.bookForm.selectedAuthors.push(a);
      }
    },
    removeAuthor(a) { this.bookForm.selectedAuthors = this.bookForm.selectedAuthors.filter(x => x.id !== a.id); },
    toggleCategory(c) {
      if (this.bookForm.selectedCategories.some(x => x.id === c.id)) {
        this.removeCategory(c);
      } else {
        this.bookForm.selectedCategories.push(c);
      }
    },
    removeCategory(c) { this.bookForm.selectedCategories = this.bookForm.selectedCategories.filter(x => x.id !== c.id); },
    toggleTranslator(t) {
      if (this.bookForm.selectedTranslators.some(x => x.id === t.id)) {
        this.removeTranslator(t);
      } else {
        this.bookForm.selectedTranslators.push(t);
      }
    },
    removeTranslator(t) { this.bookForm.selectedTranslators = this.bookForm.selectedTranslators.filter(x => x.id !== t.id); },

    // Validate book form
    validateBookForm() {
      let valid = true;
      this.bookFormErrors = { name: '', content: '', authors: '', categories: '' };
      if (!this.bookForm.name.trim()) { this.bookFormErrors.name = 'Tên sách không được bỏ trống.'; valid = false; }
      if (!this.bookForm.content.trim() || this.bookForm.content.trim().length < 10) {
        this.bookFormErrors.content = 'Tóm tắt phải có ít nhất 10 ký tự.'; valid = false;
      }
      if (!this.bookForm.coverFile) { toast.error('Vui lòng chọn ảnh bìa.'); valid = false; }
      if (this.bookForm.selectedAuthors.length === 0) { this.bookFormErrors.authors = 'Vui lòng chọn ít nhất 1 tác giả.'; valid = false; }
      if (this.bookForm.selectedCategories.length === 0) { this.bookFormErrors.categories = 'Vui lòng chọn ít nhất 1 thể loại.'; valid = false; }
      return valid;
    },

    // Submit approve
    async submitApprove() {
      if (!this.validateBookForm() || this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        const formData = new FormData();
        formData.append('cover', this.bookForm.coverFile);
        formData.append('suggestId', String(this.selectedSuggest.id));

        const bookDto = {
          name: this.bookForm.name.trim(),
          content: this.bookForm.content.trim(),
          status: 0,
          linkShopee: this.bookForm.linkShopee.trim(),
          authors: this.bookForm.selectedAuthors.map(a => a.id),
          categories: this.bookForm.selectedCategories.map(c => c.id),
          translators: this.bookForm.selectedTranslators.map(t => t.id)
        };
        formData.append('data', JSON.stringify(bookDto));

        await bookSuggestApi.createFromSuggest(formData);
        toast.success('Đã tạo sách và duyệt đề xuất thành công!');
        this.closeApproveModal();
        this.selectedSuggest = null;
        this.loadSuggests(this.currentPage);
        this.fetchPendingCount();
      } catch (err) {
        toast.error(err?.message || 'Có lỗi xảy ra.');
        this.closeApproveModal();
        this.selectedSuggest = null;
        this.loadSuggests(this.currentPage);
        this.fetchPendingCount();
      } finally {
        this.isSubmitting = false;
      }
    },

    // ── REJECT MODAL ─────────────────────────────────────────────────────────
    openRejectModal(item) {
      this.selectedSuggest = item;
      this.rejectNote = '';
      this.rejectModalVisible = true;
    },
    closeRejectModal() {
      this.rejectModalVisible = false;
      this.rejectNote = '';
    },
    async submitReject() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        await bookSuggestApi.reject({
          id: this.selectedSuggest.id,
          adminNote: this.rejectNote.trim(),
          updateTime: this.selectedSuggest.updateTime || this.selectedSuggest.update_time || ''
        });
        toast.success('Đã từ chối đề xuất.');
        this.closeRejectModal();
        this.selectedSuggest = null;
        this.loadSuggests(this.currentPage);
        this.fetchPendingCount();
      } catch (err) {
        toast.error(err?.message || 'Có lỗi xảy ra.');
        this.closeRejectModal();
        this.selectedSuggest = null;
        this.loadSuggests(this.currentPage);
        this.fetchPendingCount();
      } finally {
        this.isSubmitting = false;
      }
    },

    // ── HELPERS ───────────────────────────────────────────────────────────────
    getStatusLabel(status) {
      const map = { 0: 'Chờ duyệt', 1: 'Đã duyệt', 2: 'Từ chối' };
      return map[status] || 'Không rõ';
    },
    getStatusBadgeClass(status) {
      if (status === 0) return 'status-badge-pending-del';
      if (status === 1) return 'status-badge-approved';
      if (status === 2) return 'status-badge-deleted';
      return '';
    },
    formatDateTime(str) {
      if (!str) return '';
      const s = String(str).trim();
      if (/^\d{14}$/.test(s)) {
        const y = s.substring(0, 4);
        const m = s.substring(4, 6);
        const d = s.substring(6, 8);
        const h = s.substring(8, 10);
        const min = s.substring(10, 12);
        const sec = s.substring(12, 14);
        return `${h}:${min}:${sec} ${d}/${m}/${y}`;
      }
      if (/^\d{12}$/.test(s)) {
        const y = s.substring(0, 4);
        const m = s.substring(4, 6);
        const d = s.substring(6, 8);
        const h = s.substring(8, 10);
        const min = s.substring(10, 12);
        return `${h}:${min}:00 ${d}/${m}/${y}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const parts = s.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return s;
    }
  }
};
